<!--
    Image Processor Component
    - Custom element for bulk image processing and optimization
    - Uses Jimp for client-side image processing
    - Supports multiple formats, resizing, quality adjustment, and B&W conversion
    - shadow: "none" disables Shadow DOM to inherit global Bulma styles
-->
<svelte:options customElement={{ tag: "image-processor", shadow: "none" }} />

<script lang="ts">
    import { ImageProcessor } from "../../lib/image-processor";
    import type { Settings, ImageFile, ProcessingProgress } from "../../lib/image-processor";

    // ============================================================================
    // COMPONENT STATE
    // ============================================================================

    let settings: Settings = $state({
        outputFormat: "jpeg",
        quality: 85,
        maxWidth: null,
        maxHeight: null,
        convertToBlackAndWhite: false,
    });

    let images: ImageFile[] = $state([]);
    let isProcessing: boolean = $state(false);
    let isDragging: boolean = $state(false);
    let processingProgress: ProcessingProgress | null = $state(null);

    let fileInput: HTMLInputElement | undefined = $state();

    // ============================================================================
    // FILE HANDLING
    // ============================================================================

    function handleFileSelect(event: Event): void {
        const target = event.target as HTMLInputElement;
        if (target.files) {
            addFiles(Array.from(target.files));
        }
    }

    function handleDrop(event: DragEvent): void {
        event.preventDefault();
        isDragging = false;

        if (event.dataTransfer?.files) {
            addFiles(Array.from(event.dataTransfer.files));
        }
    }

    function handleDragOver(event: DragEvent): void {
        event.preventDefault();
        isDragging = true;
    }

    function handleDragLeave(): void {
        isDragging = false;
    }

    function addFiles(files: File[]): void {
        // Filter for image files only
        const imageFiles = files.filter((file) => file.type.startsWith("image/"));

        // Create ImageFile objects
        const newImages: ImageFile[] = imageFiles.map((file) => ({
            id: crypto.randomUUID(),
            file,
            status: "pending",
            progress: 0,
            previewUrl: URL.createObjectURL(file),
        }));

        images = [...images, ...newImages];
    }

    function removeImage(id: string): void {
        const image = images.find((img) => img.id === id);
        if (image?.previewUrl) {
            URL.revokeObjectURL(image.previewUrl);
        }
        images = images.filter((img) => img.id !== id);
    }

    function clearAll(): void {
        // Revoke all preview URLs
        images.forEach((img) => {
            if (img.previewUrl) {
                URL.revokeObjectURL(img.previewUrl);
            }
        });
        images = [];
        processingProgress = null;
    }

    function resetForReprocess(): void {
        // Reset completed images back to pending status
        images.forEach((img) => {
            if (img.status === "completed") {
                img.status = "pending";
                img.progress = 0;
            }
        });
        images = [...images];
    }

    function openFilePicker(): void {
        fileInput?.click();
    }

    // ============================================================================
    // IMAGE PROCESSING
    // ============================================================================

    async function processAllImages(): Promise<void> {
        if (images.length === 0 || isProcessing) return;

        isProcessing = true;

        for (let i = 0; i < images.length; i++) {
            const image = images[i];

            // Skip already processed or errored images
            if (image.status === "completed" || image.status === "error") {
                continue;
            }

            // Update processing progress
            processingProgress = {
                currentFileIndex: i + 1,
                totalFiles: images.length,
                currentFileName: image.file.name,
                currentFileProgress: 0,
            };

            // Update image status
            image.status = "processing";
            images = [...images];

            try {
                // Process the image
                const blob = await ImageProcessor.processImage(image.file, settings, (progress) => {
                    image.progress = progress;
                    if (processingProgress) {
                        processingProgress.currentFileProgress = progress;
                    }
                    images = [...images];
                });

                // Generate output filename
                const filename = ImageProcessor.generateOutputFilename(image.file.name, settings);

                // Download the processed image
                ImageProcessor.downloadBlob(blob, filename);

                // Update image status
                image.status = "completed";
                image.progress = 100;
            } catch (error) {
                image.status = "error";
                image.errorMessage = error instanceof Error ? error.message : "Unknown error";
            }

            images = [...images];
        }

        isProcessing = false;
        processingProgress = null;
    }

    // ============================================================================
    // COMPUTED VALUES
    // ============================================================================

    const canProcess = $derived(images.length > 0 && !isProcessing);
    const hasImages = $derived(images.length > 0);
    const pendingCount = $derived(images.filter((img) => img.status === "pending").length);
    const processingCount = $derived(images.filter((img) => img.status === "processing").length);
    const completedCount = $derived(images.filter((img) => img.status === "completed").length);
    const errorCount = $derived(images.filter((img) => img.status === "error").length);
    const hasCompletedImages = $derived(completedCount > 0);

    // Calculate overall progress percentage
    const overallProgress = $derived(() => {
        if (images.length === 0) return 0;
        const totalProgress = images.reduce((sum, img) => sum + img.progress, 0);
        return Math.round(totalProgress / images.length);
    });
</script>

<!-- ============================================================================ -->
<!-- COMPONENT TEMPLATE -->
<!-- ============================================================================ -->

<div class="image-processor">
    <!-- Privacy Notification -->
    <div class="notification is-info is-light mb-4">
        <p>
            <strong><i class="fas fa-shield-alt mr-2"></i>Privacy Notice:</strong>
            All image processing is performed entirely in your browser. No images are uploaded to our servers. Your images remain 100% private.
        </p>
    </div>

    <!-- Toolbar -->
    <div class="box mb-4">
        <div class="columns is-multiline">
            <!-- Output Format -->
            <div class="column is-narrow">
                <div class="field">
                    <label class="label is-small">Output Format</label>
                    <div class="control">
                        <div class="select is-small">
                            <select bind:value={settings.outputFormat} disabled={isProcessing}>
                                <option value="jpeg">JPEG</option>
                                <option value="webp">WebP</option>
                                <option value="png">PNG</option>
                                <option value="bmp">BMP</option>
                                <option value="tiff">TIFF</option>
                                <option value="gif">GIF</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Quality -->
            {#if settings.outputFormat === "jpeg" || settings.outputFormat === "webp"}
                <div class="column is-narrow">
                    <div class="field">
                        <label class="label is-small">Quality (%)</label>
                        <div class="control">
                            <input type="number" class="input is-small" min="1" max="100" bind:value={settings.quality} disabled={isProcessing} style="width: 120px;" />
                        </div>
                    </div>
                </div>
            {/if}

            <!-- Max Width -->
            <div class="column is-narrow">
                <div class="field">
                    <label class="label is-small">Max Width (px)</label>
                    <div class="control">
                        <input type="number" class="input is-small" placeholder="Auto" bind:value={settings.maxWidth} disabled={isProcessing} min="1" style="width: 120px;" />
                    </div>
                </div>
            </div>

            <!-- Max Height -->
            <div class="column is-narrow">
                <div class="field">
                    <label class="label is-small">Max Height (px)</label>
                    <div class="control">
                        <input type="number" class="input is-small" placeholder="Auto" bind:value={settings.maxHeight} disabled={isProcessing} min="1" style="width: 120px;" />
                    </div>
                </div>
            </div>

            <!-- Black and White -->
            <div class="column is-narrow">
                <div class="field">
                    <label class="label is-small">&nbsp;</label>
                    <div class="control">
                        <label class="checkbox">
                            <input type="checkbox" bind:checked={settings.convertToBlackAndWhite} disabled={isProcessing} />
                            Black & White
                        </label>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Upload / Drag and Drop Area -->
    <div class="upload-area box {isDragging ? 'is-dragging' : ''} {hasImages ? 'has-images' : ''}" ondrop={handleDrop} ondragover={handleDragOver} ondragleave={handleDragLeave} onclick={openFilePicker}>
        <input type="file" bind:this={fileInput} onchange={handleFileSelect} multiple accept="image/*" style="display: none;" />

        {#if !hasImages}
            <div class="upload-prompt">
                <p class="icon is-large">
                    <i class="fas fa-cloud-upload-alt fa-3x"></i>
                </p>
                <p class="is-size-5 has-text-weight-semibold">Drop images here or click to select</p>
                <p class="is-size-7 has-text-grey">Supports PNG, JPEG, WebP, BMP, TIFF, GIF and SVG formats</p>
            </div>
        {:else}
            <div class="upload-prompt-small">
                <p class="is-size-7">
                    <i class="fas fa-plus mr-2"></i>
                    Click or drop to add more images
                </p>
            </div>
        {/if}
    </div>

    <!-- Image List -->
    {#if hasImages}
        <div class="box mt-4">
            <div class="level mb-3">
                <div class="level-left">
                    <div class="level-item">
                        <p class="is-size-6 has-text-weight-semibold">
                            Images ({images.length})
                            {#if pendingCount > 0}
                                <span class="tag is-light ml-2">{pendingCount} pending</span>
                            {/if}
                            {#if processingCount > 0}
                                <span class="tag is-info ml-2">{processingCount} processing</span>
                            {/if}
                            {#if completedCount > 0}
                                <span class="tag is-success ml-2">{completedCount} completed</span>
                            {/if}
                            {#if errorCount > 0}
                                <span class="tag is-danger ml-2">{errorCount} errors</span>
                            {/if}
                        </p>
                    </div>
                </div>
                <div class="level-right">
                    <div class="level-item">
                        <button class="button is-primary is-small mr-2" onclick={processAllImages} disabled={!canProcess}>
                            <span class="icon is-small">
                                <i class="fas fa-play"></i>
                            </span>
                            <span>Process All</span>
                        </button>
                        <button class="button is-light is-small mr-2" onclick={resetForReprocess} disabled={!hasCompletedImages || isProcessing}>
                            <span class="icon is-small">
                                <i class="fas fa-redo"></i>
                            </span>
                            <span>Reset for Reprocess</span>
                        </button>
                        <button class="button is-light is-small" onclick={clearAll} disabled={isProcessing}>
                            <span class="icon is-small">
                                <i class="fas fa-trash"></i>
                            </span>
                            <span>Clear All</span>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Processing Progress Bar -->
            {#if isProcessing && processingProgress}
                <div class="box has-background-light mb-3">
                    <p class="is-size-7 mb-2">
                        Processing: {processingProgress.currentFileName} ({processingProgress.currentFileIndex}/{processingProgress.totalFiles})
                    </p>
                    <progress class="progress is-primary" value={processingProgress.currentFileProgress} max="100">
                        {processingProgress.currentFileProgress}%
                    </progress>
                    <p class="is-size-7 has-text-grey">
                        Overall: {overallProgress()}% complete
                    </p>
                </div>
            {/if}

            <!-- Image Cards -->
            <div class="image-grid">
                {#each images as image (image.id)}
                    <div class="image-card box p-3">
                        <div class="image-preview">
                            {#if image.previewUrl}
                                <img src={image.previewUrl} alt={image.file.name} />
                            {/if}
                            <div class="image-status status-{image.status}">
                                {#if image.status === "pending"}
                                    <span class="tag is-light">Pending</span>
                                {:else if image.status === "processing"}
                                    <span class="tag is-info">Processing {image.progress}%</span>
                                {:else if image.status === "completed"}
                                    <span class="tag is-success">
                                        <i class="fas fa-check mr-1"></i>
                                        Completed
                                    </span>
                                {:else if image.status === "error"}
                                    <span class="tag is-danger">
                                        <i class="fas fa-exclamation-triangle mr-1"></i>
                                        Error
                                    </span>
                                {/if}
                            </div>
                        </div>
                        <div class="image-info mt-2">
                            <p class="is-size-7 has-text-weight-semibold" title={image.file.name}>
                                {image.file.name.length > 30 ? image.file.name.substring(0, 27) + "..." : image.file.name}
                            </p>
                            <p class="is-size-7 has-text-grey">
                                {(image.file.size / 1024).toFixed(1)} KB
                            </p>
                            {#if image.errorMessage}
                                <p class="is-size-7 has-text-danger mt-1">
                                    {image.errorMessage}
                                </p>
                            {/if}
                        </div>
                        <button class="delete-button button is-small is-light" onclick={() => removeImage(image.id)} disabled={isProcessing} title="Remove image">
                            <span class="icon is-small">
                                <i class="fas fa-times"></i>
                            </span>
                        </button>
                    </div>
                {/each}
            </div>
        </div>
    {/if}
</div>

<!-- ============================================================================ -->
<!-- COMPONENT STYLES -->
<!-- ============================================================================ -->

<style>
    .image-processor {
        display: flex;
        flex-direction: column;
        width: 100%;
    }

    .upload-area {
        border: 2px dashed #dbdbdb;
        border-radius: 6px;
        padding: 3rem;
        text-align: center;
        cursor: pointer;
        transition: all 0.2s ease;
        background-color: #fafafa;
    }

    .upload-area:hover {
        border-color: #b5b5b5;
        background-color: #f0f0f0;
    }

    .upload-area.is-dragging {
        border-color: #3273dc;
        background-color: #eef6fc;
    }

    .upload-area.has-images {
        padding: 1.5rem;
    }

    .upload-prompt {
        color: #7a7a7a;
    }

    .upload-prompt-small {
        color: #7a7a7a;
    }

    .image-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
        grid-auto-rows: 1fr;
        gap: 1rem;
    }

    .image-card {
        position: relative;
        display: flex;
        flex-direction: column;
        height: 100%;
    }

    .image-preview {
        position: relative;
        width: 100%;
        height: 150px;
        overflow: hidden;
        border-radius: 4px;
        background-color: #f5f5f5;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .image-preview img {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
    }

    .image-status {
        position: absolute;
        top: 0.5rem;
        right: 0.5rem;
    }

    .image-info {
        flex: 1;
    }

    .delete-button {
        margin-top: 0.5rem;
        width: 100%;
    }

    .slider {
        width: 150px;
    }
</style>
